import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import type { NotificationPriority } from '../../constants/notification.constants';
import { BaseEntity } from './base.entity';
import { UserEntity } from './user.entity';

/**
 * In-app notification targeted at a single user (DD.9.5.1). Drives
 * the global notification bell + future per-user notification list
 * page. Distinct from:
 *
 *   - **Communications Panel** (DD.9.5.2): the persistent
 *     "unhandled stuff" surface — voicemails not listened to,
 *     unread chats. The Communications Panel does NOT consume rows
 *     from this table; it derives its state from the underlying
 *     resources directly so dismissing a notification can't make
 *     the persistent indicator vanish.
 *   - **`NotificationEntity` / `NotificationGroupEntity`** (legacy,
 *     unused): designed for SMS/phone notification routing in the
 *     legacy Workd app. Heavyweight group/member/state model with a
 *     legacy `workdUserId` foreign key, mute flag, phone-topic
 *     joins. Doesn't fit a per-user direct in-app inbox; this
 *     lighter entity is purpose-built for that.
 *   - **`AlertBannerEntity`**: tenant-wide announcement banners,
 *     not per-user.
 *
 * Naming convention follows `BaseActivityEntity` — `entityType` +
 * `entityId` for the polymorphic "what this is about" pointer, so
 * the same `(entityType, entityId) → URL` resolver can be reused
 * across both surfaces.
 */
@Index('user_notification_user_unread_idx', ['fkUserId', 'isRead'])
@Index('user_notification_user_created_idx', ['fkUserId', 'createdDate'])
@Index('user_notification_user_dismissed_idx', ['fkUserId', 'dismissedAt'])
@Index('user_notification_dedupe_idx', ['fkUserId', 'dedupeKey'])
@Entity('user_notification')
export class UserNotificationEntity extends BaseEntity {
	@Column('int', { name: 'fk_user__id', unsigned: true })
	public fkUserId!: number;

	/**
	 * Specific event verb — describes WHAT happened. Examples:
	 * `'voicemail_arrived'`, `'order_approval_required'`, `'mention'`,
	 * `'invoice_overdue'`. The render layer maps this to an icon
	 * (same pattern `BaseActivityEntity.activityType` uses).
	 */
	@Column('varchar', { name: 'notificationType', length: 64 })
	public notificationType!: string;

	/**
	 * Broad bucket for filtering + per-user notification preferences.
	 * Free-form string today (no enum constraint at the DB level) so
	 * new categories can land without a migration; values are
	 * coordinated via a constants file consumed by both writers and
	 * the future preferences UI.
	 *
	 * Conventional values:
	 *   - `'phone'`         — voicemail, missed call, transfer
	 *   - `'sales'`         — orders, quotes, leads, opportunities
	 *   - `'approvals'`     — pending approval / approved / denied
	 *   - `'financial'`     — payments, invoices, chargebacks
	 *   - `'collaboration'` — mentions, notes, task assignments
	 *   - `'operations'`    — inventory, reorder alerts
	 *   - `'system'`        — automation failures, integration sync
	 *   - `'ai'`            — AI summaries, AI escalations
	 */
	@Column('varchar', { name: 'category', length: 32 })
	public category!: string;

	/**
	 * Sort weight + visual emphasis. `'urgent'` sorts above
	 * `'normal'` which sorts above `'info'`; the bell badge can
	 * surface only urgents, etc.
	 */
	@Column('varchar', {
		name: 'priority',
		length: 16,
		default: () => "'normal'",
	})
	public priority!: NotificationPriority;

	@Column('varchar', { name: 'title', length: 255 })
	public title!: string;

	@Column('text', { name: 'body', nullable: true })
	public body!: string | null;

	/**
	 * Polymorphic "what this notification is about" pointer. Matches
	 * `BaseActivityEntity` so a single render-time resolver can map
	 * `(entityType, entityId)` to a URL for both notification clicks
	 * and activity-history clicks.
	 *
	 * Examples: `'order' / 123`, `'call' / 456`, `'invoice' / 789`,
	 * `'client' / 42`, `'chat_conversation' / 17`.
	 *
	 * Both nullable: a system-wide notification (e.g., "Maintenance
	 * window Friday") doesn't link to any specific entity.
	 */
	@Column('varchar', { name: 'entityType', length: 64, nullable: true })
	public entityType!: string | null;

	@Column('int', { name: 'entityId', unsigned: true, nullable: true })
	public entityId!: number | null;

	@Column('tinyint', { name: 'isRead', width: 1, default: () => "'0'" })
	public isRead!: boolean;

	@Column('datetime', { name: 'readAt', nullable: true })
	public readAt!: Date | null;

	/**
	 * When the user explicitly dismissed (the × on a row, or "Clear
	 * all") this notification, hiding it from the bell list. Distinct
	 * from `readAt`: a notification can be READ but still listed
	 * (greyed, under the "Earlier" group) — dismissing removes it from
	 * the tray entirely. Null = not dismissed.
	 *
	 * Soft-hide, not a hard delete: the row is retained so history and
	 * the `expiresAt` janitor still apply. (Soft-delete via
	 * BaseEntity's `_deleted_date` is reserved for true deletion; a
	 * user dismissing a notification is a lighter, list-only action.)
	 */
	@Column('datetime', { name: 'dismissedAt', nullable: true })
	public dismissedAt!: Date | null;

	/**
	 * Optional TTL. Notifications with `expiresAt < NOW()` should be
	 * filtered out of the bell list and may be hard-deleted by a
	 * janitor. Useful for noisy categories (KPI alerts, system
	 * messages) so they don't accumulate forever.
	 */
	@Column('datetime', { name: 'expiresAt', nullable: true })
	public expiresAt!: Date | null;

	/**
	 * Idempotency / collapse key. When a writer supplies a
	 * `dedupeKey` and a row with the same `(fkUserId, dedupeKey)`
	 * already exists, the writer service updates that row in place
	 * instead of inserting a duplicate. Two use cases:
	 *
	 *   - **Webhook idempotency**: Twilio retries the recording-
	 *     status webhook on transient failures. Setting
	 *     `dedupeKey = "voicemail-${callId}"` makes the second
	 *     fire a no-op refresh instead of stacking duplicate
	 *     "voicemail arrived" notifications.
	 *   - **Collapsing repeated events**: an admin can replace
	 *     "New order #123 needs approval" with "5 new orders need
	 *     approval" by re-writing the same dedupe key with a new
	 *     title.
	 *
	 * Dedupe is scoped per-user (the index includes `fkUserId`).
	 * Two different users can hold rows with the same `dedupeKey`
	 * — they're independent notifications.
	 */
	@Column('varchar', { name: 'dedupeKey', length: 128, nullable: true })
	public dedupeKey!: string | null;

	@ManyToOne(() => UserEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_user__id', referencedColumnName: 'id' }])
	public fkUser!: UserEntity;
}
